/* eslint-disable @typescript-eslint/naming-convention */
import assertNever from 'assert-never';

import {
	FirmwareUpdateJobRequest,
	InventoryDevice,
	PropertyPanelDevice,
	ScheduledJob,
	ScheduledJobRequest,
	ScheduledJobType
} from '@shure/cloud/shared/models/devices';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';

import { AnalyticsCommonDeviceInfo, DeviceRowDataForAnalytics } from './models/common.model';
import {
	AnalyticsCommonScheduledJobInfo,
	AnalyticsFirmwareUpdateJobRequest,
	AnalyticsScheduledJobRequest
} from './models/scheduled-jobs-event.model';

export function propertyPanelDeviceToAnalyticsDevice(devices: PropertyPanelDevice[]): AnalyticsCommonDeviceInfo[] {
	return devices.map((device) => ({
		model: device.model,
		serial: device.features.serialNumber?.serialNumber,
		mac: device.features.audioNetwork?.macAddress,
		hardware_id: device.hardwareId,
		network_status: device.status
	}));
}

export function inventoryDeviceToAnalyticsDevice(devices: InventoryDevice[]): AnalyticsCommonDeviceInfo[] {
	return devices.map((device) => ({
		model: device.model,
		serial: device.features.serialNumber?.serialNumber,
		mac: device.features.audioNetwork?.macAddress,
		hardware_id: device.hardwareId,
		network_status: device.status
	}));
}

export function deviceRowDataToAnalyticsDevice(devices: DeviceRowDataForAnalytics[]): AnalyticsCommonDeviceInfo[] {
	return devices.map((device) => ({
		model: <DeviceModel>device.model,
		serial: device.serialNumber,
		mac: device.macAddress,
		hardware_id: device.hardwareId,
		network_status: device.deviceStatusCellData?.status
	}));
}

//
// Scheduled Job Utilities
//
export function mapScheduledJobTypeToAnalyticsType(jobType: ScheduledJobType): ScheduledJobType {
	switch (jobType) {
		case ScheduledJobType.FirmwareUpdate:
			return ScheduledJobType.FirmwareUpdate;
		default:
			assertNever(jobType);
	}
}

export function buildAnalyticsScheduledJob(
	job: ScheduledJob,
	requests?: ScheduledJobRequest[]
): AnalyticsCommonScheduledJobInfo {
	return {
		job_id: job.jobId,
		type: mapScheduledJobTypeToAnalyticsType(job.type),
		status: job.status,
		created_by: job.createdBy,
		created_at: job.createdAt?.toISOString(),
		updated_by: job.updatedBy,
		updated_at: job.updatedAt?.toISOString(),
		request_count: job.requestCount,
		scheduled_time: job.executeAt.toISOString(),
		requests: requests ? requests.map((request) => buildAnalyticsScheduledJobRequest(request, job.type)) : []
	};
}

export function buildAnalyticsScheduledJobRequest(
	request: ScheduledJobRequest,
	jobType: ScheduledJobType
): AnalyticsScheduledJobRequest {
	switch (jobType) {
		case ScheduledJobType.FirmwareUpdate:
			return buildAnalyticsFirmwareUpdateJobRequest(<FirmwareUpdateJobRequest>request);
		default:
			return assertNever(jobType);
	}
}

function buildAnalyticsFirmwareUpdateJobRequest(request: FirmwareUpdateJobRequest): AnalyticsFirmwareUpdateJobRequest {
	return {
		device: {
			model: request.task.device.model,
			network_status: request.task.device.status,
			hardware_id: request.task.device.hardwareId
		},
		hosted: request.task.hosted,
		current_firmware_version: request.task.device.currentFirmwareVersion,
		update_to_firmware_package_key: request.task.packageKey,
		update_to_firmware_version: request.task.packageVersion
	};
}
